import { saveJson } from '../../../../services/fileSystemService'

/** An MCP mock configuration — the backend's eval `scenario` entity. */
export type McpMock = {
  scenario_id: string
  name: string
  version_id: string
  body: Record<string, unknown>
  created_at: string
  version_created_at: string
  used_by_cases: number
}

export type McpMockListResponse = {
  scenarios: McpMock[]
  total: number
  limit: number
  offset: number
}

/** Page size for the server-side paginated mock list (backend caps limit at 50). */
export const MCP_MOCKS_PAGE_SIZE = 25

export type McpMockFilters = {
  /** Case-insensitive name search; sent to the backend as `q`. */
  q?: string
  page: number
  pageSize: number
}

export const getDefaultMcpMockFilters = (): McpMockFilters => ({
  page: 1,
  pageSize: MCP_MOCKS_PAGE_SIZE,
})

/** Build the query string for the list endpoint: name search + pagination. */
export const buildMcpMocksListQuery = (filters: McpMockFilters): URLSearchParams => {
  const query = new URLSearchParams()
  const q = filters.q?.trim()
  if (q) {
    query.set('q', q)
  }
  query.set('limit', String(filters.pageSize))
  query.set('offset', String((filters.page - 1) * filters.pageSize))
  return query
}

export type McpMockCreateInput = {
  name: string
  body: Record<string, unknown>
}

export type McpMockUpdateInput = {
  scenario_id: string
  body: Record<string, unknown>
}

/** Mirrors the mock-server save-time limit; keeps an oversized paste/file out. */
export const MCP_MOCK_UPLOAD_MAX_BYTES = 1_048_576 // 1 MiB

type ParsedMockFile = {
  body?: Record<string, unknown>
  error?: string
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/** Parse uploaded file text into a mock body, returning a friendly error string instead of throwing. */
export const parseMockFile = (text: string): ParsedMockFile => {
  let parsed: unknown
  try {
    parsed = JSON.parse(text)
  } catch {
    return { error: 'The file is not valid JSON.' }
  }
  if (!isPlainObject(parsed)) {
    return { error: 'The file must contain a JSON object.' }
  }
  return { body: parsed }
}

/** The backend keys responses on the body's top-level `scenario` field; that is the mock's name. */
export const deriveMockName = (body: Record<string, unknown>): string | null => {
  const scenario = body.scenario
  if (typeof scenario !== 'string') {
    return null
  }
  const trimmed = scenario.trim()
  return trimmed.length > 0 ? trimmed : null
}

/**
 * Pre-flight the backend's save-time rule (422 otherwise): the row name must
 * equal the body's `scenario` field. Returns an error message, or null if valid.
 */
export const validateMockUpload = (
  name: string,
  body: Record<string, unknown>,
): string | null => {
  const trimmedName = name.trim()
  if (!trimmedName) {
    return 'Name is required.'
  }
  const scenarioName = deriveMockName(body)
  if (!scenarioName) {
    return 'The file is missing a top-level "scenario" name.'
  }
  if (scenarioName !== trimmedName) {
    return `The name must match the file's "scenario" field (${scenarioName}).`
  }
  return null
}

/**
 * Build the PUT payload for editing a mock. The backend update is body-only —
 * the row name is immutable and the body's `scenario` field must still match it
 * (enforced by `validateMockUpload` before this is called).
 */
export const buildMockUpdateInput = (
  mock: McpMock,
  body: Record<string, unknown>,
): McpMockUpdateInput => ({
  scenario_id: mock.scenario_id,
  body,
})

/** Build a safe download filename from the mock name. */
export const mockFilename = (name: string): string => {
  const slug = name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return `${slug || 'mcp-mock'}.json`
}

export const serializeMockBody = (body: Record<string, unknown>): string =>
  JSON.stringify(body, null, 2)

/** Download a mock's body as a pretty-printed JSON file named after the mock. */
export const downloadMockJson = (mock: McpMock): void => {
  saveJson(serializeMockBody(mock.body), mockFilename(mock.name))
}
